import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { branchService } from '@/data/branchService';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import {
  ExternalProductMapping,
  ExternalSyncLog,
  ExternalSyncStatus,
  ExternalSyncType,
  EXTERNAL_SYNC_STATUS_COLOR,
  EXTERNAL_SYNC_STATUS_LABELS,
  EXTERNAL_SYNC_TRIGGER_LABELS,
  EXTERNAL_SYNC_TYPE_LABELS,
  FoodIntegration,
  FOOD_PROVIDER_LABELS,
  SyncRunResult
} from '@/types/FoodDelivery.interface';
import { Branch } from '@/types/Branch.interface';
import FoodDeliveryEmptyState from '../food-delivery/components/FoodDeliveryEmptyState';
import { FOOD_DELIVERY_ROUTES, headerBranchId } from '../food-delivery/helpers';

// "Hedef availability platforma yansımış mı?" — lastSyncedAvailable, operatörün
// platformAvailable hedefinden farklıysa drift vardır (stok-out senkronu bekliyor).
const availabilityDrift = (m: ExternalProductMapping): boolean =>
  m.lastSyncedAvailable === null ||
  m.lastSyncedAvailable !== m.platformAvailable;

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('tr-TR') : '—';

const FoodDeliverySync: React.FC = () => {
  const { isBranchAdmin, isAdminView, currentBranch } = useUserViewMode();

  const [branches, setBranches] = useState<Branch[]>([]);
  const headerBranch = headerBranchId(currentBranch, isAdminView);
  const [branchId, setBranchId] = useState<number | null>(headerBranch);
  // Üst menüden şube değişince sayfa da o şubeye geçer.
  useEffect(() => {
    if (headerBranch) setBranchId(headerBranch);
  }, [headerBranch]);
  const [integrations, setIntegrations] = useState<FoodIntegration[]>([]);
  const [integrationId, setIntegrationId] = useState<number | null>(null);

  const [mappings, setMappings] = useState<ExternalProductMapping[]>([]);
  const [logs, setLogs] = useState<ExternalSyncLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastRun, setLastRun] = useState<SyncRunResult | null>(null);
  const [toast, setToast] = useState<{
    msg: string;
    severity: 'success' | 'error' | 'info';
  } | null>(null);

  // Şube listesi (admin/şirket görünümü).
  useEffect(() => {
    if (isBranchAdmin) return;
    branchService
      .getAllFlat({ getAll: true })
      .then(setBranches)
      .catch(() =>
        setToast({ msg: 'Şubeler getirilemedi.', severity: 'error' })
      );
  }, [isBranchAdmin]);

  // Şube seçilince entegrasyonları getir.
  useEffect(() => {
    if (!branchId) {
      setIntegrations([]);
      setIntegrationId(null);
      return;
    }
    foodDeliveryService
      .listByBranch(branchId)
      .then((list) => {
        setIntegrations(list);
        setIntegrationId(list[0]?.id ?? null);
      })
      .catch(() =>
        setToast({ msg: 'Entegrasyonlar getirilemedi.', severity: 'error' })
      );
  }, [branchId]);

  const loadData = useCallback(async () => {
    if (!integrationId) {
      setMappings([]);
      setLogs([]);
      return;
    }
    setLoading(true);
    try {
      const [pm, sl] = await Promise.all([
        foodDeliveryService.listMappings(integrationId),
        foodDeliveryService.listSyncLogs(integrationId, { limit: 20 })
      ]);
      setMappings(pm);
      setLogs(sl);
    } catch {
      setToast({ msg: 'Veriler getirilemedi.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    setLastRun(null);
    loadData();
  }, [loadData]);

  const handleSync = async (types?: ExternalSyncType[]) => {
    if (!integrationId) return;
    setSyncing(true);
    setLastRun(null);
    try {
      const res = await foodDeliveryService.runSync(integrationId, types);
      setLastRun(res);
      const pushed = res.results.reduce((s, r) => s + r.itemsPushed, 0);
      const skipped = res.results.every(
        (r) => r.status === ExternalSyncStatus.SKIPPED
      );
      const failed =
        res.results.length > 0 &&
        res.results.every((r) => r.status === ExternalSyncStatus.FAILED);
      setToast({
        msg: failed
          ? 'Senkron başarısız.'
          : skipped
            ? 'Senkron atlandı (capability/credential yok ya da senkron kapalı).'
            : `Senkron tamam: ${pushed} kalem platforma gönderildi.`,
        severity: failed ? 'error' : skipped ? 'info' : 'success'
      });
      await loadData();
    } catch {
      setToast({ msg: 'Senkron başarısız.', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageTitleWrapper>
        <Box>
          <Typography variant="h3" component="h3" gutterBottom>
            Fiyat & Uygunluk Senkronu
          </Typography>
          <Typography variant="subtitle2">
            Core fiyat ve stok durumunu platforma yansıtın. Stok biten ürünler
            platformda otomatik pasif edilir; yalnız değişen (drift olan)
            kalemler gönderilir.
          </Typography>
        </Box>
      </PageTitleWrapper>

      <Box sx={{ px: 4, pb: 4 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
            >
              {!isBranchAdmin && (
                <Autocomplete
                  sx={{ minWidth: 280 }}
                  options={branches}
                  getOptionLabel={(b) => b.name}
                  value={branches.find((b) => b.id === branchId) ?? null}
                  onChange={(_e, val) => setBranchId(val?.id ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Şube seç" />
                  )}
                />
              )}
              <Autocomplete
                sx={{ minWidth: 280 }}
                options={integrations}
                getOptionLabel={(i) =>
                  `${FOOD_PROVIDER_LABELS[i.providerCode]}${
                    i.displayName ? ` — ${i.displayName}` : ''
                  }`
                }
                value={integrations.find((i) => i.id === integrationId) ?? null}
                onChange={(_e, val) => setIntegrationId(val?.id ?? null)}
                disabled={!branchId}
                renderInput={(params) => (
                  <TextField {...params} label="Entegrasyon seç" />
                )}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="outlined"
                onClick={() => handleSync([ExternalSyncType.AVAILABILITY])}
                disabled={!integrationId || syncing}
              >
                Sadece Uygunluk
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleSync([ExternalSyncType.PRICE])}
                disabled={!integrationId || syncing}
              >
                Sadece Fiyat
              </Button>
              <Button
                variant="contained"
                startIcon={
                  syncing ? <CircularProgress size={18} /> : <SyncIcon />
                }
                onClick={() => handleSync()}
                disabled={!integrationId || syncing}
              >
                Senkronize Et
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {!integrationId ? (
          branchId && integrations.length === 0 ? (
            <FoodDeliveryEmptyState
              title="Entegrasyon yok"
              message="Bu şubede senkronlanacak yemek platformu entegrasyonu yok. Önce Yemek Platformları ekranından bir entegrasyon oluşturun."
            />
          ) : (
            <Alert severity="info">
              Senkron için bir şube ve entegrasyon seçin.
            </Alert>
          )
        ) : (
          <>
            {lastRun && (
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                {lastRun.results.map((r) => (
                  <Chip
                    key={r.type}
                    color={EXTERNAL_SYNC_STATUS_COLOR[r.status]}
                    label={`${EXTERNAL_SYNC_TYPE_LABELS[r.type]}: ${EXTERNAL_SYNC_STATUS_LABELS[r.status]} — gönderilen ${r.itemsPushed}/${r.itemsDrifted} (hata ${r.itemsFailed})`}
                  />
                ))}
              </Stack>
            )}

            {/* Eşleşme durumu + drift göstergesi */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Eşleşme Durumu ({mappings.length})
                </Typography>
                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : mappings.length === 0 ? (
                  <FoodDeliveryEmptyState
                    message="Bu entegrasyonda ürün eşleşmesi yok; senkronlanacak bir şey bulunamaz. Önce menüyü çekip ürünleri eşleştirin."
                    to={FOOD_DELIVERY_ROUTES.mappings}
                    actionLabel="Menü Eşleştirme’ye git"
                  />
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Core Ürün</TableCell>
                          <TableCell>Platform Ürün ID</TableCell>
                          <TableCell align="right">
                            Platform Fiyatı (son push)
                          </TableCell>
                          <TableCell align="center">Uygunluk</TableCell>
                          <TableCell>Son Senkron</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mappings.map((m) => {
                          const drift = availabilityDrift(m);
                          return (
                            <TableRow key={m.id} hover>
                              <TableCell>
                                {m.companyProduct?.name ??
                                  `#${m.companyProductId}`}
                              </TableCell>
                              <TableCell>{m.externalProductId}</TableCell>
                              <TableCell align="right">
                                {m.platformPrice ?? '—'}
                                {m.lastSyncedPrice != null && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    display="block"
                                  >
                                    son: {m.lastSyncedPrice} ₺
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  color={
                                    m.platformAvailable ? 'success' : 'default'
                                  }
                                  label={m.platformAvailable ? 'Açık' : 'Pasif'}
                                />
                                {drift && (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label="senkron bekliyor"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>{fmtDate(m.lastSyncedAt)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* Senkron logları */}
            <Card>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Senkron Logları
                </Typography>
                {logs.length === 0 ? (
                  <Alert severity="info">Henüz senkron çalışması yok.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tarih</TableCell>
                          <TableCell>Tip</TableCell>
                          <TableCell>Tetikleyen</TableCell>
                          <TableCell>Durum</TableCell>
                          <TableCell align="right">İncelenen</TableCell>
                          <TableCell align="right">Drift</TableCell>
                          <TableCell align="right">Gönderilen</TableCell>
                          <TableCell align="right">Hata</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {logs.map((l) => (
                          <TableRow key={l.id} hover>
                            <TableCell>{fmtDate(l.createdAt)}</TableCell>
                            <TableCell>
                              {EXTERNAL_SYNC_TYPE_LABELS[l.type]}
                            </TableCell>
                            <TableCell>
                              {EXTERNAL_SYNC_TRIGGER_LABELS[l.trigger]}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={EXTERNAL_SYNC_STATUS_COLOR[l.status]}
                                label={EXTERNAL_SYNC_STATUS_LABELS[l.status]}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {l.itemsChecked}
                            </TableCell>
                            <TableCell align="right">
                              {l.itemsDrifted}
                            </TableCell>
                            <TableCell align="right">{l.itemsPushed}</TableCell>
                            <TableCell align="right">{l.itemsFailed}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FoodDeliverySync;
